import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/admin/components/ui/avatar';
import type { User } from '@/admin/lib/api';
import { cn } from '@/admin/lib/utils';
import { useMemo } from 'react';

export interface UserAvatarProps {
  user: User;
  showName?: boolean;
  showEmail?: boolean;
  // Optional secondary text to show instead of or in addition to email
  description?: React.ReactNode;

  className?: string;
  avatarClassName?: string;

  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function UserAvatar({
  user,
  showName = false,
  showEmail = false,
  description,
  className,
  avatarClassName,
  size = 'md',
}: UserAvatarProps) {
  const initials = useMemo(() => {
    const name = user.display_name || user.email || '?';
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, [user.display_name, user.email]);

  const sizeClasses = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-14 w-14',
  };

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Avatar className={cn(sizeClasses[size], avatarClassName)}>
        <AvatarImage
          src={user.avatar_url ?? undefined}
          alt={user.display_name ?? undefined}
        />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>

      {(showName || showEmail || description) && (
        <div className="grid flex-1 text-left leading-tight">
          {showName && (
            <span className="truncate font-medium text-sm">
              {user.display_name || (!showEmail && user.email) || 'Unnamed'}
            </span>
          )}
          {showEmail && user.email && (
            <span className="truncate text-xs text-muted-foreground">
              {user.email}
            </span>
          )}
          {description && (
            <span className="truncate text-xs text-muted-foreground">
              {description}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
